import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import ReservationDateSelect from 'ui/utils/ReservationDateSelect'
import { ReservationListItem } from 'types/med'
import Stack from 'ui/layout/Stack'
import {
  formatTimeString,
  parseDateString,
  parseDatetimeSecondString,
} from 'ui/utils/dateFormatUtils'
import { ReservationStatusLabelsKeys } from 'types/common'
import { Box } from '@mui/material'
import { useParams } from 'react-router-dom'
import { Text1 } from 'ui/shorthands/Texts'
import { reservationStatusLabelText } from 'ui/partials/reservationStatusLabelText'
import { jstNewDate } from 'ui/utils/jstDatetime'

const reformatToTime = (str: string): string =>
  formatTimeString(parseDatetimeSecondString(str))

const useStyles = makeStyles()((theme: Theme) => ({
  dateSelect: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  listContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
    paddingBottom: 76, //NOTE: wovnタグで必要な高さ
  },
  item: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    boxSizing: 'border-box',
    height: 80,
    cursor: 'pointer',
  },
  itemGray: {
    color: theme.palette.action.disabledBackground,
  },
  itemActive: {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light,
  },
  row: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
  },
  department: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    marginLeft: theme.spacing(0.5),
  },
  status: {
    fontSize: theme.typography.pxToRem(12),
    flex: 'auto',
    marginLeft: theme.spacing(0.5),
    textAlign: 'right',
    letterSpacing: '0.4px',
  },
  name: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  age: {
    marginLeft: theme.spacing(0.5),
  },
  empty: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    padding: theme.spacing(2),
  },
}))

const SidebarReservationListItem: React.FC<{
  reservation: ReservationListItem
  onClickItem?: (reservation: ReservationListItem) => void
  active?: boolean
}> = ({ reservation, onClickItem, active }) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()

  const startTimeString = useMemo(
    () => reformatToTime(reservation.reservation_datetime_start),
    [reservation.reservation_datetime_start]
  )

  const endTimeString = useMemo(
    () => reformatToTime(reservation.reservation_datetime_end),
    [reservation.reservation_datetime_end]
  )

  const isGrayed = (
    ['canceled', 'billed', 'finish_no_billing'] as ReservationStatusLabelsKeys[]
  ).includes(reservation.status)

  return (
    <Box onClick={() => onClickItem?.(reservation)}>
      <Stack
        borderBottom
        className={cx(
          classes.item,
          isGrayed && classes.itemGray,
          active && classes.itemActive
        )}
      >
        <div className={classes.row}>
          <div>
            {startTimeString}〜{endTimeString}
          </div>
          <div className={classes.department}>
            {reservation.reservation_department}
          </div>
          <div className={classes.status}>
            {reservationStatusLabelText(reservation)}
          </div>
        </div>

        <div className={classes.row}>
          <span className={classes.name}>
            {reservation.applicant_last_name}
            {reservation.applicant_first_name}
          </span>
          <span className={classes.age}>
            {reservation.applicant_age}
            {t('歳')}
          </span>
        </div>
      </Stack>
    </Box>
  )
}

export type SidebarReservationListProps = {
  /** 予約情報一覧 */
  reservations?: ReservationListItem[] | null

  /** 日付変更時のイベントハンドラ */
  onChangeDate: (date: Date) => void

  /** 選択中のリストアイテム */
  selectedId: ReservationListItem['id'] | null

  /** リストアイテムクリック時のイベントハンドラ */
  onClickItem: (reservation: ReservationListItem) => void
}
const SidebarReservationList: React.FC<SidebarReservationListProps> = ({
  reservations,
  onChangeDate,
  onClickItem,
  selectedId,
}: SidebarReservationListProps) => {
  const { t } = useTranslation()
  const { date } = useParams<{ date?: string }>()

  const [selectedDate, _setSelectedDate] = useState(
    date ? parseDateString(date) : jstNewDate
  )
  const { classes } = useStyles()

  const setSelectedDate = (newDate: Date) => {
    onChangeDate?.(newDate)
    _setSelectedDate(newDate)
  }
  useEffect(() => {
    date && _setSelectedDate(parseDateString(date))
  }, [date])

  return (
    <>
      <ReservationDateSelect
        selectedDate={selectedDate}
        onSelect={setSelectedDate}
        className={classes.dateSelect}
      />
      <div className={classes.listContainer}>
        {reservations?.length ? (
          reservations?.map((reservation) => (
            <SidebarReservationListItem
              key={reservation.id}
              reservation={reservation}
              onClickItem={onClickItem}
              active={reservation.id === selectedId}
            />
          ))
        ) : (
          <div className={classes.empty}>
            <Text1>{t('予約がありません')}</Text1>
          </div>
        )}
      </div>
    </>
  )
}

export default SidebarReservationList
